import { Sanity$BlogPage } from '@otovo/shared/types/sanityTypes';
import cx from 'classnames';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { FormattedMessage as T, useIntl } from 'react-intl';
import m from '../messages';

type Props = {
  sanityDocument: Sanity$BlogPage;
};

const scrollToId = (id: string) => {
  const el = document.getElementById(id);
  const navbarOffset = 100;
  const top = el.getBoundingClientRect().top + window.scrollY - navbarOffset;

  window.scrollTo({ top, behavior: 'smooth' });
};

const BlogTableOfContents = ({ sanityDocument }: Props) => {
  const [toc, setToc] = useState([]);
  const intl = useIntl();

  useEffect(() => {
    if (!sanityDocument) return;

    // Parse HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(
      sanityDocument.content.code,
      'text/html',
    );

    // Select the heading levels you want in the TOC
    const headings = doc.querySelectorAll('h2, h3, h4');

    const items = [];
    headings.forEach((el) => {
      if (!el.id) return; // TOC requires IDs

      items.push({
        id: el.id,
        text: el.textContent.replace(/\s+/g, ' ').trim(),
        level: el.tagName.toLowerCase(),
      });
    });

    setToc(items);
  }, [sanityDocument]);

  return (
    <nav
      aria-label={intl.formatMessage(m.tableOfContentsHeader)}
      id="toc"
      className="toc"
      itemScope
      itemType="https://schema.org/SiteNavigationElement"
    >
      <h2 className="mb-3 text-xl font-semibold">
        <T {...m.tableOfContentsHeader} />
      </h2>
      <ul className="space-y-2">
        {toc.map((item) => (
          <li
            key={item.id}
            className={cx({
              'pl-0 text-base': item.level === 'h2',
              'pl-4 text-sm': item.level === 'h3',
            })}
          >
            <Link
              itemProp="url"
              href={`#${item.id}`}
              onClick={(e) => {
                e.preventDefault();
                scrollToId(item.id);
              }}
            >
              <span itemProp="name">{item.text}</span>
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default BlogTableOfContents;
